<script>
import {Icon} from '@iconify/vue';
import useVuelidate from '@vuelidate/core';
import {required} from '@vuelidate/validators';

/* COMPOSANTS */
import AkInputText from '@components/input/AkInputText';
import AkDialog from '@components/general/AkDialog';
import GaView from '@components/layout/GaView';

/* MIXINS */
import randomRef from '@mixins/randomRef';
import utilsMixin from '@mixins/utilsMixin';
import loaderMixin from '@mixins/loaderMixin';
import messageMixin from '@mixins/messageMixin';

/* SERVICES */
import itemLabelService from '@services/itemLabelService';
import motifRefusService from '@services/motifRefusService';

export default {
  components: {
    Icon,
    AkDialog,
    AkInputText,
    GaView,
  },
  mixins: [loaderMixin, messageMixin, randomRef, utilsMixin],
  head() {
    return {
      title: 'settings.motifrefus',
    };
  },
  setup() {
    return {v$: useVuelidate()};
  },
  data() {
    return {
      loading: true,
      list: [],
      item: {},
    };
  },
  computed: {},
  mounted() {
    motifRefusService.list().then((data) => {
      this.list = data;
      this.loading = false;
    });
  },
  validations() {
    return {
      item: {
        label: {required},
        type: {required},
      },
    };
  },
  methods: {
    create() {
      this.item.type = 'MOTIFREFUS';
      this.v$.$touch();
      if (this.v$.$error) return;

      this.showTotalLoader();
      itemLabelService
        .create(this.item)
        .then((data) => {
          this.list.push(data);
          this.success(this.$t('motifrefus.added'));
        })
        .finally(() => {
          this.hideLoader();
          this.$refs.dialogCreate.hide();
        });
    },
    update() {
      this.v$.$touch();
      if (this.v$.$error) return;

      this.showTotalLoader();
      itemLabelService
        .update(this.item)
        .then((data) => {
          this.replaceInListById(this.list, data);
          this.success(this.$t('motifrefus.updated'));
        })
        .finally(() => {
          this.hideLoader();
          this.$refs.dialogEdit.hide();
        });
    },
    delete() {
      itemLabelService
        .delete(this.item)
        .then((data) => {
          this.removeInListById(this.list, data.id);
          this.success(this.item.label + ' a bien été supprimé');
        })
        .catch((e) => {
          if (e.response.status === 406) {
            this.addError('La item est liée à un modèle de cahier des charges, et ne peut être supprimée');
          } else {
            this.addError(this.$t('unknow_error'));
          }
        });
    },

    openCreateDialog() {
      this.item = {};
      this.$refs.dialogCreate.show();
    },
    openEditDialog(item) {
      this.item = {...item};
      this.$refs.dialogEdit.show();
    },
    openDeleteDialog(item) {
      this.item = item;
      this.$refs.dialogDelete.show();
    },
  },
};
</script>

<template>
  <GaView :ref="ref" :title="$t('settings.motifrefus')">
    <template #action>
      <div class="d-flex justify-content-end">
        <button class="btn btn-primary float-right" @click="openCreateDialog()">
          <i class="gst-plus pr-1" />
          {{ $t('add') }}
        </button>
      </div>
    </template>

    <template #content>
      <DataTable
        :always-show-paginator="false"
        :loading="loading"
        :paginator="true"
        :rows="10"
        :rows-per-page-options="[10, 20, 50]"
        :value="list"
        class="table"
        current-page-report-template="{first}-{last}/{totalRecords}"
        removable-sort
        responsive-layout="scroll"
        sort-field="position"
        striped-rows>
        <template #empty>
          {{ $t('motifrefus.empty') }}
        </template>
        <Column :header="$t('item_label.label')">
          <template #body="slotProps">
            {{ slotProps.data.label }}
          </template>
        </Column>

        <Column body-style="text-align: right; overflow: visible" header-style=" width: 160px; text-align: btn">
          <template #body="slotProps">
            <div class="d-flex justify-content-end">
              <span class="btn btn-xs btn-inverse-primary mr-1 pointer" @click="openEditDialog(slotProps.data)">
                <i class="fe fe-edit" />
              </span>
              <span class="btn btn-xs btn-inverse-danger pointer" @click="openDeleteDialog(slotProps.data)">
                <i class="fe fe-trash" />
              </span>
            </div>
          </template>
        </Column>
      </DataTable>
    </template>

    <template #extra>
      <AkDialog
        ref="dialogCreate"
        :title="$t('motifrefus.add')"
        :validate-label="$t('add')"
        width="450px"
        @validate="create()">
        <div class="form-row">
          <AkInputText
            v-model="item.label"
            :label="$t('motifrefus.item')"
            :validator="v$.item.label"
            class-name="col-12" />
        </div>
      </AkDialog>

      <AkDialog
        ref="dialogEdit"
        :title="$t('motifrefus.update')"
        :validate-label="$t('update')"
        width="450px"
        @validate="update()">
        <div class="form-row">
          <AkInputText
            v-model="item.label"
            :label="$t('motifrefus.item')"
            :validator="v$.item.label"
            class-name="col-12" />
        </div>
      </AkDialog>

      <AkDialog
        ref="dialogDelete"
        :auto-hide-on-validate="true"
        :cancel-label="$t('no')"
        :title="$t('delete_dialog')"
        :validate-label="$t('yes')"
        width="450px"
        @validate="this.delete()">
        <div class="flex items-center gap-2">
          <Icon icon="fluent:warning-20-regular" class="text-red" width="26" />
          <span>{{ $t('motifrefus.confirm_delete') }}</span>
        </div>
      </AkDialog>
    </template>
  </GaView>
</template>
